import {
  APK_TRANSFER_CLOCK_SKEW_MS,
  APK_TRANSFER_HASH_KEY,
  APK_TRANSFER_MAX_AGE_MS,
} from "../install/apkTransferCodec";
import { setRestoredPid } from "./runtime";
import {
  getAttributionScope,
  getAttributionStorage,
  getAttributionTarget,
  readAttributionRecord,
  writeAttributionRecord,
} from "./storage";
import type { AttributionStorageTarget } from "./storage";

export interface RestoredPidSessionTarget extends AttributionStorageTarget {
  location: { href: string; hash: string };
}

interface RestoredPidSessionRecord {
  v: 1;
  pid: string;
  issuedAtMs: number;
}

const STORAGE_PREFIX = "webhall:restored-pid:v1:";

const isValidRecord = (
  value: unknown,
  nowMs: number
): value is RestoredPidSessionRecord => {
  const record = value as RestoredPidSessionRecord | undefined;
  return (
    record?.v === 1 &&
    typeof record.pid === "string" &&
    !!record.pid.trim() &&
    typeof record.issuedAtMs === "number" &&
    Number.isFinite(record.issuedAtMs) &&
    record.issuedAtMs > 0 &&
    nowMs - record.issuedAtMs <= APK_TRANSFER_MAX_AGE_MS &&
    record.issuedAtMs - nowMs <= APK_TRANSFER_CLOCK_SKEW_MS
  );
};

/** Preserve a validated native handoff only for reloads in this browsing session. */
export const persistRestoredPidForSession = (
  pid: string,
  issuedAtMs: number,
  target?: RestoredPidSessionTarget
): void => {
  const win = getAttributionTarget(target);
  if (!win) {
    return;
  }

  const scope = getAttributionScope(win);
  if (!scope) {
    return;
  }

  writeAttributionRecord(
    getAttributionStorage(win, "sessionStorage"),
    `${STORAGE_PREFIX}${scope}`,
    { v: 1, pid: pid.trim(), issuedAtMs }
  );
};

/** Run before URL restoration so any incoming packet supersedes the old session. */
export const bootstrapRestoredPidSession = (
  target?: RestoredPidSessionTarget,
  nowMs = Date.now()
): void => {
  const win = getAttributionTarget(target);
  if (!win) {
    return;
  }

  const scope = getAttributionScope(win);
  if (!scope) {
    return;
  }

  const storage = getAttributionStorage(win, "sessionStorage");
  const key = `${STORAGE_PREFIX}${scope}`;
  const hashParams = new URLSearchParams(
    new URL(win.location.href).hash.replace(/^#/, "")
  );
  if (hashParams.has(APK_TRANSFER_HASH_KEY)) {
    setRestoredPid(undefined, win);
    writeAttributionRecord(storage, key);
    return;
  }

  const cached = readAttributionRecord(storage, key);
  if (isValidRecord(cached, nowMs)) {
    setRestoredPid(cached.pid, win);
  } else {
    writeAttributionRecord(storage, key);
  }
};
